import { CommonModule } from '@angular/common';
import { Component, inject, type OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DWD_RETRY_WINDOW_MS,
  FORCEABLE_BLOCK_VERDICT_LABELS,
  FORCEABLE_BLOCK_VERDICTS,
  type ForceableBlockVerdict,
  LICENSE_QA_SCENARIOS,
  type LicenseQaScenario,
  QA_DWD_OVERRIDE_STATUSES,
  QA_FORCEABLE_ENDPOINT_LABELS,
  QA_INVITE_REJECT_LABELS,
  QA_INVITE_REJECT_MODES,
  QA_MAX_SPINNER_DELAY_MS,
  QA_OVERRIDE_TEST_DOMAINS,
  QA_SYNC_OUTCOMES,
  type QaDeleteCustomerPreview,
  type QaDwdOverrideStatus,
  type QaForceableEndpoint,
  type QaInviteRejectMode,
  type QaOverridesState,
  type QaSyncOutcome,
  scenarioSimulatesUnavailable,
} from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import {
  CdwConfirmationDialogComponent,
  type ConfirmationDialogResult,
} from '@cdw-ae/common-ui-components-dialog';
import { take } from 'rxjs';
import { dwdStatusLabel } from '../../../../common/services/platform-customer/dwd-status';
import { PlatformLicensingService } from '../../../../common/services/platform-licensing/platform-licensing.service';
import { PlatformQaService } from '../../../../common/services/platform-qa/platform-qa.service';

/**
 * How long every override on this page lives before it self-heals — the
 * server's QA_OVERRIDE_TTL_SECONDS, worded for humans. One copy so the page's
 * copy can't disagree with itself.
 */
export const QA_TTL_LABEL = '~1 hour';

/**
 * INTERNAL QA Overrides (EDUS-2726/2681): test-only actions confined to the
 * fixed internal test customers (QA_OVERRIDE_TEST_DOMAINS). A shared test-domain
 * selector drives each section:
 *   - License — seed a Chargify scenario to drive any login verdict.
 *   - Terms & Conditions — clear acceptances so Wizard Step 1 can be replayed.
 * New QA-only sections plug in here; the server-side allowlist is the safety
 * control on every action.
 */
@Component({
  selector: 'cdw-qa-overrides',
  standalone: true,
  imports: [CommonModule, FormsModule, CdwButtonModule],
  templateUrl: './qa-overrides.component.html',
  styleUrl: './qa-overrides.component.scss',
})
export class QaOverridesComponent implements OnInit {
  private readonly licensingService = inject(PlatformLicensingService);
  private readonly qaService = inject(PlatformQaService);
  private readonly dialog = inject(MatDialog);

  readonly domains = QA_OVERRIDE_TEST_DOMAINS;
  readonly ttlLabel = QA_TTL_LABEL;
  readonly scenarios = LICENSE_QA_SCENARIOS;

  /** Shared across every section: which test customer to act on. */
  domain: string = QA_OVERRIDE_TEST_DOMAINS[0];

  // ── License section ────────────────────────────────────────────────
  scenario: LicenseQaScenario = LICENSE_QA_SCENARIOS[0].key;
  licenseBusy = false;
  licenseMessage: string | null = null;
  licenseError: string | null = null;

  // ── DWD verify-status section ──────────────────────────────────────
  readonly dwdStatuses = QA_DWD_OVERRIDE_STATUSES;
  /**
   * Same label vocabulary the customers grid and its DWD filter use, so a status
   * reads identically everywhere in the console. Every dropdown here pairs the
   * label with the raw value it writes — QA quotes those raw values in tickets
   * and reads them straight out of the DB, so both need to be on screen.
   */
  readonly dwdLabel = dwdStatusLabel;
  dwdStatus: QaDwdOverrideStatus = QA_DWD_OVERRIDE_STATUSES[0];
  dwdBusy = false;
  dwdMessage: string | null = null;
  dwdError: string | null = null;

  // ── DWD propagation re-check section (EDUS-2844) ───────────────────
  /** The re-check window in hours (24), derived from the shared constant. */
  readonly retryWindowHours = DWD_RETRY_WINDOW_MS / (60 * 60 * 1000);
  /**
   * Defaults to the full window because reaching the timed-out state is the
   * reason this exists — smaller values are for eyeballing the elapsed counter.
   */
  dwdRecheckHoursAgo = this.retryWindowHours;
  dwdRecheckBusy = false;
  dwdRecheckMessage: string | null = null;
  dwdRecheckError: string | null = null;

  // ── Login block-screen section (EDUS-2863) ─────────────────────────
  /**
   * Forces the login verdict outright, so every block screen is reachable
   * without arranging the state that normally produces it. Only BLOCK verdicts
   * are offered — the shared list holds no access-granting verdict, so this
   * lever cannot mint a session however it is driven.
   */
  readonly blockVerdicts = FORCEABLE_BLOCK_VERDICTS;
  readonly blockVerdictLabel = FORCEABLE_BLOCK_VERDICT_LABELS;
  blockVerdict: ForceableBlockVerdict = FORCEABLE_BLOCK_VERDICTS[0];
  verdictBusy = false;
  verdictMessage: string | null = null;
  verdictError: string | null = null;

  // ── Client-id load-failure section (Step-3 load error) ─────────────
  clientIdFailBusy = false;
  clientIdFailMessage: string | null = null;
  clientIdFailError: string | null = null;

  // ── Sync-kickoff-failure section ───────────────────────────────────
  syncFailBusy = false;
  syncFailMessage: string | null = null;
  syncFailError: string | null = null;

  // ── Sync terminal-outcome section (EDUS-2770) ──────────────────────
  /**
   * The other end of the sync from the kickoff-failure lever above: pretends
   * saas-jobs-api reported a terminal state, so the outcome record, the emails
   * and the retry screen are all reachable. Sits below that lever deliberately —
   * they read as a pair, and the ordering matches the order a sync goes through
   * them.
   */
  readonly syncOutcomes = QA_SYNC_OUTCOMES;
  syncOutcome: QaSyncOutcome = 'failure';
  syncOutcomeBusy = false;
  syncOutcomeMessage: string | null = null;
  syncOutcomeError: string | null = null;

  // ── Forced request failures (EDUS-2855) ────────────────────────────
  /**
   * Six independent markers, one per onboarding request, surfaced as a toggle
   * inside the section for the step each one affects rather than gathered into
   * a "forced failures" block of their own — a tester arrives here knowing which
   * STEP they are on, not which route it calls.
   *
   * One in flight at a time (`endpointFailBusy` holds which), and feedback is
   * tracked per endpoint so the message lands under the toggle that produced it
   * instead of at the bottom of the page.
   */
  readonly endpointLabel = QA_FORCEABLE_ENDPOINT_LABELS;
  endpointFailBusy: QaForceableEndpoint | null = null;
  private endpointFailMessages: Partial<Record<QaForceableEndpoint, string>> =
    {};
  private endpointFailErrors: Partial<Record<QaForceableEndpoint, string>> = {};

  // ── Step 5 Mandrill rejection (EDUS-2855) ──────────────────────────
  /**
   * The counterpart to the `invite-send` forced failure, and deliberately not
   * folded into it: that one fails the REQUEST, this one lets it succeed and
   * puts rejections in the 200 body. They exercise different client branches,
   * and the body one is where the "renders success on total failure" bug lived.
   */
  readonly inviteRejectModes = QA_INVITE_REJECT_MODES;
  readonly inviteRejectLabel = QA_INVITE_REJECT_LABELS;
  inviteRejectMode: QaInviteRejectMode = 'all';
  inviteRejectBusy = false;
  inviteRejectMessage: string | null = null;
  inviteRejectError: string | null = null;

  // ── Global spinner delay (EDUS-2855) ───────────────────────────────
  /**
   * Applies to EVERY onboarding request, which is why it sits at the top of the
   * page rather than in a step's section — it is not about any one step.
   *
   * Defaults to 3s: long enough to read a spinner, short enough that nobody
   * wonders whether they have broken the tenant.
   */
  readonly maxSpinnerDelayMs = QA_MAX_SPINNER_DELAY_MS;
  spinnerDelayMs = 3000;
  spinnerDelayBusy = false;
  spinnerDelayMessage: string | null = null;
  spinnerDelayError: string | null = null;

  // ── Terms & Conditions section ─────────────────────────────────────
  termsBusy = false;
  termsMessage: string | null = null;
  termsError: string | null = null;

  // ── Current state (the readback every lever used to lack) ──────────
  state: QaOverridesState | null = null;
  stateBusy = false;
  stateError: string | null = null;
  clearAllBusy = false;
  clearAllMessage: string | null = null;
  clearAllError: string | null = null;

  // ── Delete test customer (EDUS-3040) ───────────────────────────────
  deleteBusy = false;
  deleteMessage: string | null = null;
  deleteError: string | null = null;

  ngOnInit(): void {
    this.refreshState();
  }

  /** Re-read after any change, so the panel reflects Redis rather than intent. */
  refreshState(): void {
    this.stateBusy = true;
    this.stateError = null;
    this.qaService.getOverrides(this.domain).subscribe({
      next: (state) => {
        this.stateBusy = false;
        this.state = state;
        // Seed the input from the live value, but only when one is actually
        // set: after a clear the field deliberately keeps what was there so it
        // can be re-armed without retyping.
        if (state.spinnerDelayMs !== null) {
          this.spinnerDelayMs = state.spinnerDelayMs;
        }
      },
      error: () => {
        this.stateBusy = false;
        this.state = null;
        this.stateError = 'Could not read the current overrides.';
      },
    });
  }

  /** True when at least one lever is live — decides whether the banner exists. */
  get hasActiveOverrides(): boolean {
    return this.activeOverrideCount > 0;
  }

  /**
   * How many levers are live. Shown in the banner heading so the scale is
   * readable before anyone parses the list — "3 QA overrides are active" lands
   * differently from an unlabelled block of text.
   */
  get activeOverrideCount(): number {
    const s = this.state;
    if (!s) {
      return 0;
    }
    const single = [
      s.clientIdFail,
      s.syncKickoffFail,
      s.syncOutcome !== null,
      s.dwdStatus !== null,
      s.loginVerdict !== null,
      s.licenseScenario !== null,
      s.spinnerDelayMs !== null,
      s.inviteReject !== null,
    ].filter(Boolean).length;
    // Each armed endpoint counts separately — they are independent levers, and
    // "2 QA overrides are active" reading as one would undersell the damage.
    return single + (s.endpointFail?.length ?? 0);
  }

  /** True when this endpoint is currently forced to fail, per the last readback. */
  isEndpointFailArmed(endpoint: QaForceableEndpoint): boolean {
    return this.state?.endpointFail?.includes(endpoint) ?? false;
  }

  endpointFailMessage(endpoint: QaForceableEndpoint): string | null {
    return this.endpointFailMessages[endpoint] ?? null;
  }

  endpointFailError(endpoint: QaForceableEndpoint): string | null {
    return this.endpointFailErrors[endpoint] ?? null;
  }

  /**
   * Arm or clear the forced failure for one onboarding request.
   *
   * `where` is the screen it produces, quoted straight back in the confirmation
   * — the lever is only useful if the person who set it knows what to go and
   * look at.
   */
  setEndpointFail(
    endpoint: QaForceableEndpoint,
    enabled: boolean,
    where: string
  ): void {
    if (this.endpointFailBusy) {
      return;
    }
    this.endpointFailBusy = endpoint;
    delete this.endpointFailMessages[endpoint];
    delete this.endpointFailErrors[endpoint];
    this.qaService
      .setEndpointFailOverride(this.domain, endpoint, enabled)
      .subscribe({
        next: ({ applied }) => {
          this.endpointFailBusy = null;
          if (!applied) {
            this.endpointFailMessages[endpoint] =
              `No tenant exists for ${this.domain} yet — nothing to override.`;
          } else if (enabled) {
            this.endpointFailMessages[endpoint] =
              `Forced to fail for ${this.domain}. ${where} Expires in ${QA_TTL_LABEL}.`;
          } else {
            this.endpointFailMessages[endpoint] =
              `Cleared for ${this.domain}. That request works normally again.`;
          }
          this.refreshState();
        },
        error: () => {
          this.endpointFailBusy = null;
          this.endpointFailErrors[endpoint] =
            'Failed to update the forced-failure override. Please try again.';
        },
      });
  }

  /** Arm a Mandrill rejection for the next Step-5 send, or null to disarm. */
  setInviteReject(mode: QaInviteRejectMode | null): void {
    if (this.inviteRejectBusy) {
      return;
    }
    this.inviteRejectBusy = true;
    this.inviteRejectMessage = null;
    this.inviteRejectError = null;
    this.qaService.setInviteRejectOverride(this.domain, mode).subscribe({
      next: ({ applied }) => {
        this.inviteRejectBusy = false;
        if (!applied) {
          this.inviteRejectMessage = `No tenant exists for ${this.domain} yet — nothing to arm.`;
        } else if (!mode) {
          this.inviteRejectMessage = `Invite-rejection cleared for ${this.domain}. Invitations send for real again.`;
        } else {
          this.inviteRejectMessage =
            `Armed for ${this.domain}: the next Step-5 send returns HTTP 200 with ` +
            (mode === 'all'
              ? 'every recipient rejected and nothing sent. Expect the error banner, NOT the success view. The list is left alone, because nothing went out.'
              : 'every recipient after the first rejected. Watch the LIST, not just the banner: the addresses that were invited are removed from it, leaving only the ones that failed. ' +
                'NOTE: add at least TWO addresses — with one there is nothing after the first to reject, so the send just succeeds.') +
            ` No email is actually sent either way. Expires in ${QA_TTL_LABEL}.`;
        }
        this.refreshState();
      },
      error: () => {
        this.inviteRejectBusy = false;
        this.inviteRejectError =
          'Failed to update the invite-rejection override. Please try again.';
      },
    });
  }

  /** Slow every onboarding request down, or pass 0 to clear. */
  setSpinnerDelay(delayMs: number): void {
    if (this.spinnerDelayBusy) {
      return;
    }
    this.spinnerDelayBusy = true;
    this.spinnerDelayMessage = null;
    this.spinnerDelayError = null;
    this.qaService.setSpinnerDelay(this.domain, delayMs).subscribe({
      next: ({ applied }) => {
        this.spinnerDelayBusy = false;
        if (!applied) {
          this.spinnerDelayMessage = `No tenant exists for ${this.domain} yet — nothing to delay.`;
        } else if (delayMs > 0) {
          this.spinnerDelayMessage =
            `Every onboarding request for ${this.domain} now takes an extra ` +
            `${delayMs}ms. Walk the wizard as an SA there to see each loading ` +
            `state. Expires in ${QA_TTL_LABEL}.`;
        } else {
          this.spinnerDelayMessage = `Spinner delay cleared for ${this.domain}. Requests are full speed again.`;
        }
        this.refreshState();
      },
      error: () => {
        this.spinnerDelayBusy = false;
        this.spinnerDelayError =
          'Failed to update the spinner delay. Please try again.';
      },
    });
  }

  onDomainChange(): void {
    // Each domain has its own set of keys, so the panel is meaningless until
    // it's re-read for the newly selected one.
    this.state = null;
    this.endpointFailMessages = {};
    this.endpointFailErrors = {};
    this.deleteMessage = null;
    this.deleteError = null;
    this.refreshState();
  }

  /**
   * No tenant for the selected domain, as far as the last readback knows. Until
   * the first readback lands the button stays enabled — the preview call answers
   * the question anyway.
   */
  get noTenantForDomain(): boolean {
    return this.state !== null && this.state.customerId === null;
  }

  /**
   * Delete the selected domain's test customer (EDUS-3040): preview first, then
   * a confirmation that names the customer, lists what will go, and makes the
   * admin type the domain — a dropdown left on the wrong domain is the mistake
   * this is guarding against, and unlike every lever below, this one can't be
   * undone.
   */
  confirmDeleteCustomer(): void {
    if (this.deleteBusy) {
      return;
    }
    const domain = this.domain;
    this.deleteBusy = true;
    this.deleteMessage = null;
    this.deleteError = null;
    this.qaService.previewDeleteCustomer(domain).subscribe({
      next: (preview) => {
        this.deleteBusy = false;
        if (!preview.customer) {
          this.deleteMessage = `No tenant exists for ${domain} — nothing to delete.`;
          this.refreshState();
          return;
        }
        this.openDeleteConfirmation(preview);
      },
      error: (err: { error?: { message?: string } }) => {
        this.deleteBusy = false;
        this.deleteError =
          err?.error?.message ??
          'Could not load what would be deleted. Please try again.';
      },
    });
  }

  private openDeleteConfirmation(preview: QaDeleteCustomerPreview): void {
    const customer = preview.customer;
    if (!customer) {
      return;
    }
    const total = preview.tables.reduce((n, t) => n + t.rows, 0);
    const rows = preview.tables
      .filter((t) => t.rows > 0)
      .map(
        (t) =>
          `<li><code>${escapeHtml(t.table)}</code>: ${t.rows.toLocaleString()}</li>`
      )
      .join('');
    this.dialog
      .open(CdwConfirmationDialogComponent, {
        data: {
          title: 'Delete test customer?',
          message: '',
          htmlMessage:
            `<p><strong>${escapeHtml(customer.name)}</strong> — ${escapeHtml(customer.primaryDomain)}, ` +
            `customer ${customer.id}, Google ${escapeHtml(customer.googleId)}.</p>` +
            `<p>This permanently deletes the customer and ${total.toLocaleString()} rows, ` +
            'plus its Firestore job documents and Redis keys. Its closed-beta allowlist entry is kept. ' +
            '<strong>It cannot be undone.</strong> Signing in again afterwards behaves like a brand-new customer.</p>' +
            `<ul>${rows}</ul>`,
          confirmText: 'Delete customer',
          cancelText: 'Cancel',
          type: 'warning',
          // Default layout, not 'warning-box': the shared dialog only renders
          // the type-to-confirm input in the default layout, so with
          // 'warning-box' the confirm button could never be enabled.
          confirmButtonColor: 'warn',
          hard: true,
          requireInputConfirmation: true,
          requiredText: customer.primaryDomain,
        },
        width: '600px',
        maxWidth: '100%',
        disableClose: true,
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: ConfirmationDialogResult | undefined) => {
        if (result?.confirmed) {
          this.deleteCustomer(preview.domain);
        }
      });
  }

  private deleteCustomer(domain: string): void {
    this.deleteBusy = true;
    this.qaService.deleteCustomer(domain).subscribe({
      next: (result) => {
        this.deleteBusy = false;
        const total = result.tables.reduce((n, t) => n + t.rows, 0);
        this.deleteMessage = result.deleted
          ? `Deleted ${result.customer?.name ?? domain} (customer ${result.customer?.id}) — ` +
            `${total.toLocaleString()} rows. Signing in as a super admin at ${domain} now starts from scratch.`
          : `No tenant exists for ${domain} — nothing was deleted.`;
        if (result.cleanupErrors.length > 0) {
          this.deleteError =
            'The customer is deleted, but some cleanup failed: ' +
            result.cleanupErrors.join('; ');
        }
        this.refreshState();
      },
      error: (err: { error?: { message?: string } }) => {
        this.deleteBusy = false;
        this.deleteError =
          err?.error?.message ??
          'Delete failed — nothing was deleted. Please try again.';
      },
    });
  }

  clearAll(): void {
    if (this.clearAllBusy) {
      return;
    }
    this.clearAllBusy = true;
    this.clearAllMessage = null;
    this.clearAllError = null;
    this.qaService.clearAllOverrides(this.domain).subscribe({
      next: () => {
        this.clearAllBusy = false;
        this.clearAllMessage =
          `All overrides cleared for ${this.domain}. ` +
          'T&C acceptances were NOT touched — that one deletes data, so it stays a separate action.';
        this.refreshState();
      },
      error: () => {
        this.clearAllBusy = false;
        this.clearAllError = 'Failed to clear the overrides. Please try again.';
      },
    });
  }

  /** Arm an outcome for the next kickoff, or pass null to disarm. */
  setSyncOutcome(outcome: QaSyncOutcome | null): void {
    if (this.syncOutcomeBusy) {
      return;
    }
    this.syncOutcomeBusy = true;
    this.syncOutcomeMessage = null;
    this.syncOutcomeError = null;
    this.qaService.setSyncOutcomeOverride(this.domain, outcome).subscribe({
      next: ({ applied }) => {
        this.syncOutcomeBusy = false;
        if (!applied) {
          this.syncOutcomeMessage = `No tenant exists for ${this.domain} yet — nothing to arm.`;
        } else if (!outcome) {
          this.syncOutcomeMessage = `Sync-outcome override cleared for ${this.domain}. The next sync runs for real.`;
        } else {
          this.syncOutcomeMessage =
            `Armed ${outcome.toUpperCase()} for ${this.domain}. Run Step 4 → "Sync Data & ` +
            'Continue" as an SA there: the kickoff proceeds normally (which is what puts ' +
            'you on the notification list), no real sync is enqueued, and the outcome is ' +
            'reported straight away — so you get the email AND the screen. ' +
            (outcome === 'success'
              ? 'Expect the "Data Sync Ready" email and Step 4 unblocked.'
              : 'Expect the "Data Sync Was Unsuccessful" email and the "Data sync has failed" ' +
                'retry screen. A PARTIAL is treated as a failure here.');
        }
        this.refreshState();
      },
      error: () => {
        this.syncOutcomeBusy = false;
        this.syncOutcomeError =
          'Failed to update the sync-outcome override. Please try again.';
      },
    });
  }

  setClientIdFail(enabled: boolean): void {
    if (this.clientIdFailBusy) {
      return;
    }
    this.clientIdFailBusy = true;
    this.clientIdFailMessage = null;
    this.clientIdFailError = null;
    this.qaService.setClientIdFailOverride(this.domain, enabled).subscribe({
      next: () => {
        this.clientIdFailBusy = false;
        this.clientIdFailMessage = enabled
          ? `The DWD client-id fetch will FAIL for ${this.domain}. Open the ` +
            "wizard's Step 3 as an SA there to see the \"couldn't load your " +
            `authorization details" load-error screen. Expires in ${QA_TTL_LABEL}.`
          : `Load-error override cleared for ${this.domain}. Step 3 loads normally again.`;
        this.refreshState();
      },
      error: () => {
        this.clientIdFailBusy = false;
        this.clientIdFailError =
          'Failed to update the load-error override. Please try again.';
      },
    });
  }

  setSyncFail(enabled: boolean): void {
    if (this.syncFailBusy) {
      return;
    }
    this.syncFailBusy = true;
    this.syncFailMessage = null;
    this.syncFailError = null;
    this.qaService.setSyncFailOverride(this.domain, enabled).subscribe({
      next: ({ applied }) => {
        this.syncFailBusy = false;
        if (!applied) {
          this.syncFailMessage = `No tenant exists for ${this.domain} yet — nothing to override.`;
        } else if (enabled) {
          this.syncFailMessage =
            `Sync kickoff will FAIL for ${this.domain}. Open the wizard's Step 4 ` +
            'as an SA there and click "Sync Data" to see the failure. ' +
            `Expires in ${QA_TTL_LABEL}.`;
        } else {
          this.syncFailMessage = `Sync-fail override cleared for ${this.domain}. Kickoff works normally again.`;
        }
        this.refreshState();
      },
      error: () => {
        this.syncFailBusy = false;
        this.syncFailError =
          'Failed to update the sync-fail override. Please try again.';
      },
    });
  }

  applyVerdictOverride(): void {
    if (this.verdictBusy) {
      return;
    }
    this.verdictBusy = true;
    this.verdictMessage = null;
    this.verdictError = null;
    const verdict = this.blockVerdict;
    this.qaService.setVerdictOverride(this.domain, verdict).subscribe({
      next: () => {
        this.verdictBusy = false;
        this.verdictMessage =
          `Forced "${verdict}" for ${this.domain}. Sign in as any user at that ` +
          `domain to see the screen. Expires in ${QA_TTL_LABEL}.`;
        this.refreshState();
      },
      error: () => {
        this.verdictBusy = false;
        this.verdictError =
          'Failed to set the verdict override. Please try again.';
      },
    });
  }

  clearVerdictOverride(): void {
    if (this.verdictBusy) {
      return;
    }
    this.verdictBusy = true;
    this.verdictMessage = null;
    this.verdictError = null;
    this.qaService.clearVerdictOverride(this.domain).subscribe({
      next: () => {
        this.verdictBusy = false;
        this.verdictMessage = `Verdict override cleared for ${this.domain}. Login resolves normally again.`;
        this.refreshState();
      },
      error: () => {
        this.verdictBusy = false;
        this.verdictError =
          'Failed to clear the verdict override. Please try again.';
      },
    });
  }

  applyDwdOverride(): void {
    if (this.dwdBusy) {
      return;
    }
    this.dwdBusy = true;
    this.dwdMessage = null;
    this.dwdError = null;
    const status = this.dwdStatus;
    this.qaService.setDwdOverride(this.domain, status).subscribe({
      next: ({ applied }) => {
        this.dwdBusy = false;
        this.dwdMessage = applied
          ? `Forced DWD status "${status}" for ${this.domain}. Open the wizard's ` +
            `Step 3 → Verify as an SA at that domain to see it. Expires in ${QA_TTL_LABEL}.`
          : `No tenant exists for ${this.domain} yet — nothing to override.`;
        this.refreshState();
      },
      error: () => {
        this.dwdBusy = false;
        this.dwdError = 'Failed to set the DWD override. Please try again.';
      },
    });
  }

  clearDwdOverride(): void {
    if (this.dwdBusy) {
      return;
    }
    this.dwdBusy = true;
    this.dwdMessage = null;
    this.dwdError = null;
    this.qaService.clearDwdOverride(this.domain).subscribe({
      next: () => {
        this.dwdBusy = false;
        this.dwdMessage = `DWD override cleared for ${this.domain}. Next check runs for real.`;
        this.refreshState();
      },
      error: () => {
        this.dwdBusy = false;
        this.dwdError = 'Failed to clear the DWD override. Please try again.';
      },
    });
  }

  /**
   * Age the re-check clock so the end-of-window states are reachable now (EDUS-2844).
   *
   * The only lever for them: the DWD override forces a status, and nothing about
   * a status makes a day go by.
   */
  backdateDwdRecheck(): void {
    if (this.dwdRecheckBusy) {
      return;
    }
    this.dwdRecheckBusy = true;
    this.dwdRecheckMessage = null;
    this.dwdRecheckError = null;
    const hoursAgo = this.dwdRecheckHoursAgo;
    this.qaService.backdateDwdRecheck(this.domain, hoursAgo).subscribe({
      next: ({ applied }) => {
        this.dwdRecheckBusy = false;
        this.dwdRecheckMessage = applied
          ? `Re-check clock for ${this.domain} moved back ${hoursAgo}h. ` +
            (hoursAgo >= this.retryWindowHours
              ? `That tenant is now past the ${this.retryWindowHours}-hour mark — reload Step 3 for the ` +
                'terminal screen, or "Run re-check now" to send the stalled email.'
              : 'Reload Step 3 to see the elapsed counter.')
          : `Nothing is re-checking for ${this.domain}. Force a DWD failure above, ` +
            'then run Step 3 → Verify to start one.';
        this.refreshState();
      },
      error: () => {
        this.dwdRecheckBusy = false;
        this.dwdRecheckError =
          'Failed to backdate the re-check clock. Please try again.';
      },
    });
  }

  /** Fire one link of the re-check chain now instead of waiting up to an hour. */
  runDwdRecheck(): void {
    if (this.dwdRecheckBusy) {
      return;
    }
    this.dwdRecheckBusy = true;
    this.dwdRecheckMessage = null;
    this.dwdRecheckError = null;
    this.qaService.runDwdRecheck(this.domain).subscribe({
      next: ({ ran }) => {
        this.dwdRecheckBusy = false;
        this.dwdRecheckMessage = ran
          ? `Ran one re-check for ${this.domain}. Check the customer's detail page ` +
            'for the current state, and the tenant mailbox for any email it sent.'
          : `No tenant exists for ${this.domain} yet — nothing to re-check.`;
        this.refreshState();
      },
      error: () => {
        this.dwdRecheckBusy = false;
        this.dwdRecheckError = 'Failed to run the re-check. Please try again.';
      },
    });
  }

  applyOverride(): void {
    if (this.licenseBusy) {
      return;
    }
    this.licenseBusy = true;
    this.licenseMessage = null;
    this.licenseError = null;
    const chosen = this.scenario;
    this.licensingService.setQaOverride(this.domain, chosen).subscribe({
      next: (result) => {
        this.licenseBusy = false;
        // The 'unavailable' scenario has no facts (it simulates a fail-closed
        // outage), so don't echo tier/inChargify for it — describe the outcome.
        this.licenseMessage = scenarioSimulatesUnavailable(chosen)
          ? `Override set for ${this.domain}: the next login FAILS CLOSED ` +
            '→ "We hit a snag" (UNAVAILABLE). Sign in as an SA at that domain ' +
            `to see it. Expires in ${QA_TTL_LABEL}.`
          : `Override set for ${this.domain}: ${result.access.tier} ` +
            `(inChargify=${result.inChargify}). Sign in as an SA at that domain to see it. ` +
            `Expires in ${QA_TTL_LABEL}.`;
        this.refreshState();
      },
      error: () => {
        this.licenseBusy = false;
        this.licenseError = 'Failed to set the override. Please try again.';
      },
    });
  }

  clearOverride(): void {
    if (this.licenseBusy) {
      return;
    }
    this.licenseBusy = true;
    this.licenseMessage = null;
    this.licenseError = null;
    this.licensingService.clearQaOverride(this.domain).subscribe({
      next: () => {
        this.licenseBusy = false;
        this.licenseMessage = `Override cleared for ${this.domain}. Next login reads real Chargify state.`;
        this.refreshState();
      },
      error: () => {
        this.licenseBusy = false;
        this.licenseError = 'Failed to clear the override. Please try again.';
      },
    });
  }

  clearTerms(): void {
    if (this.termsBusy) {
      return;
    }
    this.termsBusy = true;
    this.termsMessage = null;
    this.termsError = null;
    this.qaService.clearTerms(this.domain).subscribe({
      next: ({ cleared }) => {
        this.termsBusy = false;
        this.termsMessage =
          `Cleared ${cleared} T&C acceptance(s) for ${this.domain}. ` +
          'Those users will see Wizard Step 1 again on next login.';
      },
      error: () => {
        this.termsBusy = false;
        this.termsError = 'Failed to clear T&C acceptances. Please try again.';
      },
    });
  }
}

/** For the confirmation's HTML message; every interpolated value goes through this. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
